import { Button, Card, Divider, Drawer, Flex, Text } from '@mantine/core';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { GetTransactionRes } from '@dealstack/shared/lib/generated';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { useSplitInterestDrawerController } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/SplitInterestDrawer/useSplitInterestDrawerController.ts';
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@dealstack/frontend/shared/components/Alert/Alert.tsx';

interface Props {
	opened: boolean;
	onClose: () => void;
	refreshTransactionsList: () => void;
	transaction: GetTransactionRes;
}

// CAP-2011 "Update interest transferred": splits a Both-titled transaction on a nominee security
// into its beneficial-interest and legal-title legs. Confirm-only — the cost stamping follows the
// CAP-1999 allocation rule (beneficial leg carries the full consideration, legal leg is 0) and the
// attached records follow the beneficial leg.
export const SplitInterestDrawer = ({
	opened,
	onClose,
	refreshTransactionsList,
	transaction,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const { onConfirm, splitInterestRequest } = useSplitInterestDrawerController({
		transaction,
		refreshTransactionsList,
		onClose,
	});

	const units = transaction.units ?? 0;
	const cost = transaction.cost ?? 0;

	const legRow = (label: string, legCost: number) => (
		<Flex justify="space-between" py="0.25rem">
			<Text size="B5Medium">{label}</Text>
			<Text size="B5Regular">
				{units || LONG_DASH}{' '}
				{tCapManage('transactions.split_interest.units_suffix', { defaultValue: 'units' })}{' '}
				· {legCost}
			</Text>
		</Flex>
	);

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			size="md"
			title={tCapManage('transactions.split_interest.title', {
				defaultValue: 'Update interest transferred',
			})}
			transitionProps={drawerTransitionProps}
		>
			<Flex direction="column" gap="1rem">
				<Alert state="info" size="s">
					<AlertTitle>
						{tCapManage('transactions.split_interest.alert_title', {
							defaultValue: 'This splits the transaction into two legs',
						})}
					</AlertTitle>
					<AlertDescription>
						{tCapManage('transactions.split_interest.alert_description')}
					</AlertDescription>
				</Alert>

				<Card withBorder padding="sm">
					{legRow(
						tCapManage('transactions.interest.beneficial', {
							defaultValue: 'Beneficial',
						}),
						cost
					)}
					<Divider />
					{legRow(
						tCapManage('transactions.interest.legal', { defaultValue: 'Legal' }),
						0
					)}
				</Card>

				<Flex justify="flex-end" gap="0.5rem">
					<Button variant="default" onClick={onClose}>
						{tCapManage('transactions.split_interest.cancel', {
							defaultValue: 'Cancel',
						})}
					</Button>
					<Button
						onClick={onConfirm}
						loading={splitInterestRequest.isPending}
						data-testid="confirm-split-interest"
					>
						{tCapManage('transactions.split_interest.confirm', {
							defaultValue: 'Split interest',
						})}
					</Button>
				</Flex>
			</Flex>
		</Drawer>
	);
};
